// ============================================================================
// FeedAttachments — les pieces jointes d'une publication du fil (mig 285)
// ============================================================================
// Le meme rendu que l'apercu du telephone (PhoneAttachments). Les fichiers sont
// servis par le PROXY de l'app sous Bearer (jamais une adresse signee) :
//   - une image se materialise en object URL (cache de page, getBlobObjectUrl) ;
//     tant qu'elle n'est pas resolue — ou si le stockage manque — elle reste une
//     carte avec son icone, jamais un cadre casse ;
//   - un document (PDF, .pptx) se telecharge sous son nom par un fetch
//     authentifie dedie (downloadBlobAs) ; un echec remonte en code court.
// Composant a part entiere (jamais defini dans le rendu d'une carte).

import { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import type { PublicationAttachment } from '../../hooks/useAccueil';
import { downloadBlobAs, getBlobObjectUrl } from '../../lib/blobs/objectUrl';
import { PhoneAttachments } from './PhoneAttachments';
import type { AttachmentView } from './piecesJointes';

const attachmentPath = (publicationId: string, attachmentId: string) =>
  `/api/publications/${publicationId}/attachments/${attachmentId}`;

export function FeedAttachments({
  publicationId,
  attachments,
  locale,
  t,
  onError,
}: {
  publicationId: string;
  attachments: PublicationAttachment[];
  locale: string;
  t: TFunction;
  onError: (code: string) => void;
}) {
  const [urls, setUrls] = useState<ReadonlyMap<string, string>>(new Map());
  const imageIds = useMemo(
    () => attachments.filter((a) => a.kind === 'image').map((a) => a.id),
    [attachments],
  );

  useEffect(() => {
    let alive = true;
    for (const id of imageIds) {
      void getBlobObjectUrl(attachmentPath(publicationId, id)).then((url) => {
        if (alive && url) setUrls((prev) => new Map(prev).set(id, url));
      });
    }
    return () => { alive = false; };
  }, [publicationId, imageIds]);

  const views: AttachmentView[] = attachments.map((a) => ({
    id: a.id, kind: a.kind, name: a.name, size: a.size, url: urls.get(a.id),
  }));

  function download(a: AttachmentView) {
    void downloadBlobAs(attachmentPath(publicationId, a.id), a.name).then((ok) => {
      if (!ok) onError('download_failed');
    });
  }

  return <PhoneAttachments attachments={views} locale={locale} t={t} onDownload={download} />;
}
